import { Column } from "@tanstack/react-table";
import { TableHeaderType, TableRowItem } from "./types";
import { CSSProperties } from "react";

export const calculateDynamicMinWidth = <TData extends TableRowItem>(
  visibleHeaders: TableHeaderType<TData>[],
  originalMinWidth: number,
  totalHeadersLength: number
): number => {
  // If all columns are visible, return original minWidth without calculation
  if (visibleHeaders.length === totalHeadersLength) {
    return originalMinWidth;
  }

  // Calculate total width needed for visible columns
  let totalCalculatedWidth = 0;
  let specifiedColumnsWidth = 0;
  let unspecifiedColumnsCount = 0;

  // First pass: calculate specified column widths and count unspecified ones
  visibleHeaders.forEach(col => {
    if (col.width) {
      if (typeof col.width === "string" && col.width.endsWith("%")) {
        const percentage = parseFloat(col.width.replace("%", ""));
        const pixelWidth = (percentage / 100) * originalMinWidth;
        specifiedColumnsWidth += pixelWidth;
      } else {
        // Handle px or number values
        const pixelWidth =
          typeof col.width === "number" ? col.width : parseInt(col.width.replace("px", ""));
        specifiedColumnsWidth += pixelWidth;
      }
    } else {
      unspecifiedColumnsCount++;
    }
  });

  // Calculate width for unspecified columns
  const remainingWidth = originalMinWidth - specifiedColumnsWidth;
  const unspecifiedColumnWidth =
    unspecifiedColumnsCount > 0 ? Math.max(remainingWidth / unspecifiedColumnsCount, 100) : 0;

  // Second pass: sum all column widths
  visibleHeaders.forEach(col => {
    if (col.width) {
      if (typeof col.width === "string" && col.width.endsWith("%")) {
        const percentage = parseFloat(col.width.replace("%", ""));
        totalCalculatedWidth += (percentage / 100) * originalMinWidth;
      } else {
        const pixelWidth =
          typeof col.width === "number" ? col.width : parseInt(col.width.replace("px", ""));
        totalCalculatedWidth += pixelWidth;
      }
    } else {
      totalCalculatedWidth += unspecifiedColumnWidth;
    }
  });

  return Math.max(totalCalculatedWidth, 300); // Minimum 300px for usability
};

export const calculateDynamicMinSize = <TData extends TableRowItem>(
  columnWidth: TableHeaderType<TData>["width"],
  originalMinWidth: number,
  noFloor = false
): number => {
  const baseMinSize = 100; // Absolute minimum for usability

  if (!columnWidth) {
    return baseMinSize;
  }

  const applyFloor = (value: number) => (noFloor ? value : Math.max(value, baseMinSize));

  if (typeof columnWidth === "string" && columnWidth.endsWith("%")) {
    const percentage = parseFloat(columnWidth.replace("%", ""));
    const calculatedMinSize = (percentage / 100) * originalMinWidth;
    return applyFloor(calculatedMinSize);
  }

  if (typeof columnWidth === "string" && columnWidth.endsWith("px")) {
    return applyFloor(parseInt(columnWidth.replace("px", "")));
  }

  if (typeof columnWidth === "number") {
    return applyFloor(columnWidth);
  }

  return baseMinSize;
};

export const getCommonPinningStyles = <TData>(column: Column<TData, unknown>): CSSProperties => {
  const isPinned = column.getIsPinned() === "left";

  if (!isPinned) {
    return {
      position: "relative" as const,
      zIndex: 0,
    };
  }

  return {
    boxShadow: "4px 0 4px -4px rgba(0, 0, 0, 0.1)",
    left: `${column.getStart("left")}px`,
    position: "sticky" as const,
    zIndex: 1,
  };
};
